import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getVisitById, putVisit } from "../../services/Visit";
import { giveBackUserViewVisit } from "../../helpers/DecideBetweenUserEditOrGenerated";
import { getOrganizationFields } from "../../services/Organization";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Divider, Input, LinearProgress, Skeleton, Typography } from "@mui/joy";
import { urlToLocalImageAsset } from "../../utils/SetupInitialInputs";
import MicrophoneSelect from "../../components/MicSelect";
import { useRecorder } from "../../context/MicContext";
import TimerComponent from "../../components/Timer";
import { CirclePlay, Pause, Play } from "lucide-react";
import AudioAnalyser from "../../components/audio-display/AudioIconProcessed";
import { AlertContext } from "../../components/AlertContext";
import { NoteAdd } from "@mui/icons-material";
import RenderDifferentIcons from "../../components/AccordionIcons";
import { convertJSONKeyToTitle } from "../../helpers/KeyConverters";
import AutoBuildFormSection from "../../components/AutomaticFormBuilder";
import qs from 'qs';
import NavBlocker from "../../components/NavBlocker";
import { uploadAudio } from "../../helpers/VisitRouter";



function NewVisitOrganizationFields() {
    const { visitId } = useParams();
    const navigate = useNavigate();
    const { addAlert } = useContext(AlertContext);
    const { recordingState, recordingTime, startCapture, pauseCapture, unpauseCapture, stopCapture, cleanAudio, cleanRecordingStates, retryBlob } = useRecorder();
    const [ready, setReady] = useState(false);
    const [canDictate, setCanDictate] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [startCapturePress, setStartCapturePress] = useState(false);
    const [retry, setRetry] = useState(false);

    const organizationFormFields = useRef(null);
    const initialInputs = useRef(null);
    const imageRef = useRef(null);

    const setupVisit = async () => {
        try {
            // Get visit and set the initial inputs 
            const response = await getVisitById(visitId);
            // Set inputs, decide between user_edited and generated
            initialInputs.current = giveBackUserViewVisit(response);
            initialInputs.current['title'] = response.title;
            setCanDictate(!response.transcription);
        } catch (error) {
            throw new Error(error)
        }
    }

    const setupOrganizationFormFields = async () => {
        try {
            const response = await getOrganizationFields();
            
            imageRef.current = urlToLocalImageAsset(response['settings']['logo_url']);
            organizationFormFields.current = response['form_fields'];
        } catch(error) {
            throw new Error(error)
        }
    }

    const toggleMicrophone = async () => {
        try {
            if (recordingState === 'initialized') {
                if (startCapturePress) return;
                setStartCapturePress(true);
                await startCapture();
                setStartCapturePress(false);
            } 
            // UnPause
            else if (recordingState === 'paused') {
                if (recordingTime >= 9000) {
                    addAlert("Limit of 2 hours reached", "danger");
                    return;
                }
                unpauseCapture();
            } 
            // Pause
            else if (recordingState === 'recording'){
                pauseCapture();
            } else {
                addAlert("Recording state corrupt. Try refreshing the page", "danger");
            }
        } catch (error) {
            throw new Error(error);
        }
    }

    const generateDictation = async () => {
        let uploaded = false;
        try {
            setGenerating(true);
            let blob;
            if (retry) {
                blob = retryBlob();
            } else {
                blob = await stopCapture();
            }
            const uploadResult = await uploadAudio(blob, visitId);
            if (uploadResult.success) {
                uploaded = true;
                addAlert("Audio uploaded! Working on extracting relevant information...", "success");
            } else {
                addAlert("Couldn't upload audio. Please try again", "danger");
                return;
            }

            const intervalTime = 4000; // 4 seconds
    
            const pollForResults = () => new Promise((resolve, reject) => {
                const intervalID = setInterval(async () => {
                    try {
                        const response = await getVisitById(visitId);
    
                        let allComeTrue = true;
                        for (const section of ['call_info', 'treatment_info', 'vitals_info']) {
                            if (!response) {
                                allComeTrue = false;
                                break;
                            }
                            if (!(section in response)) {
                                allComeTrue = false;
                                continue;
                            } else if ("value" in response[section].generated_result && response[section].generated_result) {
                                allComeTrue = true;
                            } else {
                                allComeTrue = false;
                            }
                        }
                        
                        if (allComeTrue) {
                            setReady(false);
                            setGenerating(false);
                            clearInterval(intervalID);
                            resolve();
                        }
                    } catch (error) {
                        clearInterval(intervalID);
                        reject(error);
                    }
                }, intervalTime);
            });
    
            await pollForResults();
            await setup();
    
            addAlert("Call, Treatment and Vitals forms autofilled", "success");
        } catch(error) {
            if (!uploaded) {
                setRetry(true);
            }
            throw new Error(error)
        }
    }

    const handleSubmit = async (event) => {
        event.preventDefault()
        const formData = new FormData(event.target);
        const values = Object.fromEntries(formData.entries());
        const nested = qs.parse(values);

        try {
            const response = await putVisit({ 
                visitId: visitId, 
                title: nested.title,
                callInfo: nested.call_info,
                treatmentInfo: nested.treatment_info,
                vitalsInfo: nested.vitals_info,
            });

            addAlert("Saved!", "success");
            setDirty(false);
            return;
        } catch(error) {
            addAlert(error.message, "danger");
        }
    }

    const handleDirty = (_) => {
        setDirty(true);
    }

    const handleInvalid = (event) => {
        addAlert(`Save failed. ${event.target.name} is invalid`, "danger")
    }
    
    // Setup organization fields
    // Setup initial inputs for visits & dictation
    const setup = async () => {
        try {
            await setupVisit();
            await setupOrganizationFormFields();
            setReady(true);
        } catch(error) {
            addAlert("Couldn't initialize form.", "danger");
            addAlert(error.message, "danger")
        }
    }

    useEffect(() => {
        setup();

        return () => {
            cleanAudio();
            cleanRecordingStates();
        }
    }, [])

    return <Box>
        <NavBlocker dirty={dirty}/>
        {ready ? <Box>
            {/* Audio Dictation logic*/}
            <Box sx= {{ my: 2, mx: 3, display: "flex", alignItems: "end", gap: 2 }}>
                <MicrophoneSelect disabled={generating || recordingState !== 'initialized'}/>
                <TimerComponent seconds={recordingTime}/>
                {recordingState === "initialized" && <Button sx={{ width: "30%", display: "flex"}} startDecorator={<CirclePlay />} disabled={!canDictate} onClick={toggleMicrophone}>
                    Start capturing
                </Button>}
                {!generating && (recordingState === "recording" || recordingState === "paused") && <AudioAnalyser raiseAudioError={() => {}}/>}
                {recordingState === "paused" && <Button onClick={toggleMicrophone} startDecorator={<Play />} sx={{ display: "flex", width: "20%" }}>
                    Unpause
                </Button>}
                {recordingState === "recording" && <Button onClick={toggleMicrophone} startDecorator={<Pause />} sx={{ display: "flex", width: "20%" }}>
                    Pause
                </Button>}
                {!generating && (recordingState === "recording" || recordingState === "paused") && <Button startDecorator={<NoteAdd />} sx={{display: "flex", width: "30%", backgroundColor: "var(--dark-blue-button)", '&:hover': {backgroundColor: 'var(--dark-blue-button-hover)', boxShadow: "0 4px 8px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.2)" }}} onClick={generateDictation}>
                    Complete
                </Button>}
                {generating && <LinearProgress variant="soft"/>}
            </Box>
            <Divider />
            <form 
            onSubmit={handleSubmit} 
            onInput={handleDirty}
            onInvalid={handleInvalid}
            autoComplete="off"
            >
                <Box sx={{ height: "80vh", overflowY: "scroll"}}>
                    <img src={imageRef.current} alt="Organization Logo" style={{height: "10vh", minHeight: "85px"}}/>
                    <Typography sx={{py: 1, px: 4}}> 
                        <Typography level="h4" sx={{ pr: 2 }}>
                            Instructions:
                        </Typography>
                            Use smart dictate to help autofill the Patient Care Report. You can then review the information captured manually. <br />
                            Please note that any changes made must be saved. If you enter any information, please save it before submitting your dictation to prevent your changes from being overriden.
                    </Typography>
                    <Input name="title" defaultValue={initialInputs.current['title']} sx={{ mx: 2, my: 1 }}/>
                    <Divider />
                    {Object.keys(organizationFormFields.current).map((key) => {
                        return <Box sx={{ mx: 2, my: 1 }}>
                            <Accordion variant="outlined" sx={{borderBottom: "1px #c5cfd3 solid", borderRadius: "10px"}} key={key}>
                                <AccordionSummary sx={{
                                    '.MuiAccordionSummary-button': {
                                        borderRadius: "10px"
                                    }}}
                                >
                                    <Box sx={{display: "flex", pl: 1.5, gap: 1, alignItems: "center"}}>
                                        <RenderDifferentIcons label={key}/>
                                        <h3>{convertJSONKeyToTitle(key)}</h3>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{overFlowX: "scroll",
                                    '.MuiAccordionDetails-content': {
                                        overflowX: 'auto'
                                    }
                                }}>
                                    <AutoBuildFormSection formFields={organizationFormFields.current[key]} initialInput={initialInputs.current[key]} section={key}/>
                                </AccordionDetails>
                            </Accordion>
                        </Box>
                    })}
                </Box>
                <Box sx={{ display: "flex", gap: 2 }}>
                    <Button variant="plain" sx={{ backgroundColor: "white", width: "25%"}} onClick={() => { navigate(-1) }}>
                        Back
                    </Button>
                    <Button type="submit" sx={{ width: "75%" }}>
                        Save
                    </Button>
                </Box>
            </form>
        </Box> : <Skeleton />}
    </Box>
}


export default NewVisitOrganizationFields;